import dynamic, { type DynamicOptionsLoadingProps } from 'next/dynamic';
import { createContext, useContext } from 'react';
import type { ModalProps } from 'shared/components/modal-runtime.web';
import { Modal as LoadingModal, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Button } from 'shared/components/button';
import { Loader } from 'shared/components/loader';
import { Text } from 'shared/components/text';

type LoadingContextValue = {
  isVisible: boolean;
  onCancel?: () => void;
};

const LoadingContext = createContext<LoadingContextValue>({ isVisible: false });

function ModalLoading({ error, retry, pastDelay }: DynamicOptionsLoadingProps) {
  const { isVisible, onCancel } = useContext(LoadingContext);
  const { t } = useTranslation('common');

  if (!error && !pastDelay) return null;

  // RN Web owns focus trapping, Escape and the portal while the animated modal
  // runtime loads. Keep this fallback in the initial bundle so failures can retry.
  return (
    <LoadingModal visible={isVisible} transparent onRequestClose={onCancel}>
      <View className="bg-brand-brown-dark/70 flex-1 items-center justify-center p-3">
        <View
          className="bg-brand-cream-light w-full max-w-sm gap-3 rounded-lg p-5"
          testID="modal-loading"
        >
          {error ? (
            <Text accessibilityRole="alert">{t('notify_error_description')}</Text>
          ) : (
            <Loader />
          )}
          <View className="flex-row justify-end gap-3">
            {onCancel && <Button text={t('cancel')} color="secondary" onPress={onCancel} />}
            {error && retry && <Button text={t('retry')} color="brown" onPress={retry} />}
          </View>
        </View>
      </View>
    </LoadingModal>
  );
}

let modalImport: Promise<typeof import('shared/components/modal-runtime.web')> | null = null;

export function preloadModal() {
  if (!modalImport)
    modalImport = import('shared/components/modal-runtime.web').catch((error) => {
      modalImport = null;
      throw error;
    });
  return modalImport;
}

const LoadedModal = dynamic<ModalProps>(
  () =>
    preloadModal().then(({ default: LoadedModal }) => {
      return function ModalWithDefaults(props: ModalProps) {
        return <LoadedModal {...props} />;
      };
    }),
  { ssr: false, loading: ModalLoading },
);

export default function Modal({
  onLoadingCancel,
  animationInTiming = 160,
  animationOutTiming = 80,
  backdropTransitionInTiming = animationInTiming,
  backdropTransitionOutTiming = animationOutTiming,
  ...props
}: ModalProps & { onLoadingCancel?: () => void }) {
  return (
    <LoadingContext.Provider value={{ isVisible: !!props.isVisible, onCancel: onLoadingCancel }}>
      <LoadedModal
        {...props}
        animationInTiming={animationInTiming}
        animationOutTiming={animationOutTiming}
        backdropTransitionInTiming={backdropTransitionInTiming}
        backdropTransitionOutTiming={backdropTransitionOutTiming}
      />
    </LoadingContext.Provider>
  );
}
